<template>
    <div>
        <v-form ref="inviteEmailsForm" :value="valid" @input="$emit('update:valid', $event)">
            <v-textarea
                :value="value"
                :rules="nonEmptyRules"
                required
                @input="$emit('input', $event)"
                rows="3"
                auto-grow
                hint="Enter one or more email addresses, separated by commas, spaces, semicolons, or new lines."
                :disabled="disabled"
                outlined>
                <template v-slot:label>Email addresses</template>
            </v-textarea>
        </v-form>

        <v-checkbox v-if="showGenerateLinkOnlyToggle && isSysadmin" v-model="generateLinkOnlyProxy" class="mb-1 mt-0 pa-0" hide-details>
            <template v-slot:label>
                <div class="d-flex align-center">
                    Generate invitations but don't send emails
                    <v-chip class="ml-3" color="info" label outlined x-small>Sysadmin</v-chip>
                </div>
            </template>
        </v-checkbox>

        <v-alert v-if="showEmailValidationError" color="error" icon="warning" class="mt-4" text>
            <div class="d-flex flex-column">
                <span class="font-weight-bold">Email Validation Error</span>
                User invitation has failed because one or more emails could not be validated, please try again later.
            </div>
        </v-alert>

        <v-alert v-if="showInvalidEmailsWarning" type="warning" icon="warning" class="mt-4" text>
            <div class="d-flex flex-column">
                <span class="font-weight-bold">Invalid Emails</span>
                One or more of the provided emails might be invalid.
            </div>
        </v-alert>

        <InvitationErrorsDialog
            :showDialog="showDialog"
            :errorType="errorType"
            :invalidEmails="invalidEmails"
            @proceedWithTheInvitation="$emit('proceedWithTheInvitation', $event)"
            @validateEmailsAgain="$emit('validateEmailsAgain', $event)"
            @inviteWithoutFetchingDistributed="$emit('inviteWithoutFetchingDistributed', $event)"
            @fetchAndInviteAgain="$emit('fetchAndInviteAgain', $event)" />
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

const InvitationErrorsDialog = () => import('@/components/InvitationErrorsDialog.vue')

export default {
    name: 'InviteEmailsInput',
    components: { InvitationErrorsDialog },
    props: {
        value: {
            type: String,
            default: ''
        },
        valid: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        showGenerateLinkOnlyToggle: {
            type: Boolean,
            default: false
        },
        generateInvitationLinksOnly: {
            type: [Boolean, Number],
            default: false
        },
        invitationErrorState: {
            type: Object,
            default: () => ({})
        }
    },
    computed: {
        ...mapGetters(['isSysadmin']),
        nonEmptyRules() {
            return [v => (v && v.length > 0) || 'Please provide a value.']
        },
        generateLinkOnlyProxy: {
            get() {
                return this.generateInvitationLinksOnly
            },
            set(value) {
                this.$emit('update:generateInvitationLinksOnly', value)
            }
        },
        showDialog() {
            return Boolean(this.invitationErrorState?.showDialog)
        },
        errorType() {
            return this.invitationErrorState?.errorType || null
        },
        showEmailValidationError() {
            if (this.invitationErrorState?.emailsValidationError || this.invitationErrorState?.backendError) {
                return true
            }
            return this.errorType === 'backendError'
        },
        showInvalidEmailsWarning() {
            if (this.invitationErrorState?.foundInvalidEmails) {
                return true
            }
            return this.errorType === 'invalidEmails'
        },
        invalidEmails() {
            return Array.isArray(this.invitationErrorState?.invalidEmails) ? this.invitationErrorState.invalidEmails : []
        }
    },
    methods: {
        resetValidation() {
            if (this.$refs.inviteEmailsForm) {
                this.$refs.inviteEmailsForm.resetValidation()
            }
        }
    }
}
</script>
